import { useTranslation } from "@redotech/redo-i18n";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
} from "@redotech/redo-web/components/ui/popover";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as darkModeCss from "@redotech/redo-web/theme/component-colors-dark.module.css";
import classNames from "classnames";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useNowAt } from "../../use-now-at";
import * as css from "./navigation-move-callout.module.css";

interface NavigationMoveCalloutProps {
  children: ReactNode;
  anchorSelector?: string;
  href?: string;
  title: string;
  description: string;
  storageKey: string;
  dismissText?: string;
  viewText?: string;
  dismissOnVisit?: boolean;
  expiresAt?: number;
  dismissTrackId?: string;
  viewTrackId?: string;
  onDismiss?: () => void;
  onView?: () => void;
}

export const NavigationMoveCallout = memo(function NavigationMoveCallout({
  children,
  anchorSelector,
  href,
  title,
  description,
  storageKey,
  dismissText,
  viewText,
  dismissOnVisit = true,
  expiresAt,
  onDismiss,
  onView,
  dismissTrackId,
  viewTrackId,
}: NavigationMoveCalloutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [seen, setSeen] = useLocalStorage(storageKey, false);
  const now = useNowAt(expiresAt);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const setAnchorContainer = useCallback(
    (container: HTMLDivElement | null) => {
      setAnchorElement(
        anchorSelector
          ? (container?.querySelector<HTMLElement>(anchorSelector) ?? null)
          : null,
      );
    },
    [anchorSelector],
  );
  const virtualRef = useMemo(
    () => ({ current: anchorElement }),
    [anchorElement],
  );
  const onDestination = Boolean(
    href && (pathname === href || pathname.startsWith(`${href}/`)),
  );
  useEffect(() => {
    if (dismissOnVisit && onDestination && !seen) {
      setSeen(true);
    }
  }, [dismissOnVisit, onDestination, seen, setSeen]);
  const dismiss = () => {
    setSeen(true);
    onDismiss?.();
  };
  const view = () => {
    setSeen(true);
    onView?.();
    if (href) {
      navigate(href);
    }
  };
  const open =
    !seen &&
    (expiresAt === undefined || now < expiresAt) &&
    !(dismissOnVisit && onDestination) &&
    (!anchorSelector || !!anchorElement);
  return (
    <Popover
      modal={false}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          dismiss();
        }
      }}
      open={open}
    >
      {anchorSelector ? (
        <>
          <Flex
            className={open ? css.rowHighlighted : undefined}
            dir="column"
            gap="none"
            ref={setAnchorContainer}
          >
            {children}
          </Flex>
          <PopoverAnchor virtualRef={virtualRef} />
        </>
      ) : (
        <PopoverAnchor className={open ? css.highlighted : undefined}>
          {children}
        </PopoverAnchor>
      )}
      <PopoverContent
        align="center"
        aria-label={title}
        className={classNames(css.content, darkModeCss.darkMode)}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        side="right"
        sideOffset={16}
      >
        <Flex
          bgColor="primary"
          borderRadius="lg"
          dir="column"
          gap="lg"
          p="lg"
          shadow="lg"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {description}
            </Text>
          </Flex>
          <Flex gap="md" justify="flex-end">
            <RedoButton
              dataTrackId={dismissTrackId}
              hierarchy={href ? "secondary" : "primary"}
              onClick={dismiss}
              size="xs"
              text={dismissText ?? t("Dismiss")}
            />
            {href && (
              <RedoButton
                dataTrackId={viewTrackId}
                hierarchy="primary"
                onClick={view}
                size="xs"
                text={viewText ?? t("View now")}
              />
            )}
          </Flex>
        </Flex>
        <PopoverArrow className={css.arrow} height={8} width={16} />
      </PopoverContent>
    </Popover>
  );
});
